<template>
  <div class="reporte-page">
    <!-- Header -->
    <div class="page-header mb-4">
      <div class="d-flex align-center">
        <div class="header-icon-pill mr-3">
          <v-icon color="white" size="22">mdi-chart-bar</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-0">Reporte Saldo Banca</h1>
          <p class="page-subtitle mb-0">Estado financiero de tus vendedores</p>
        </div>
      </div>
    </div>

    <!-- Panel de filtros -->
    <v-card class="glass-card rounded-xl pa-5 mb-4" elevation="0">
      <div class="d-flex align-center mb-4">
        <div class="section-pill mr-2">
          <v-icon color="white" size="14">mdi-filter</v-icon>
        </div>
        <span class="section-title">Filtros</span>
      </div>
      <v-form ref="form" v-model="valid">
        <v-row align="center">
          <v-col cols="12" sm="5">
            <v-dialog ref="dialog" v-model="dateModal" :return-value.sync="dates" persistent width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="datesText" label="Período" prepend-inner-icon="mdi-calendar"
                  readonly required :rules="dateRules" v-bind="attrs" v-on="on"
                  outlined rounded dense hide-details="auto" class="modern-input" />
              </template>
              <v-date-picker v-model="dates" range>
                <v-spacer />
                <v-btn text color="primary" @click="dateModal=false">Cancelar</v-btn>
                <v-btn text color="primary" @click="$refs.dialog.save(dates); setDatesFormat()">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-col>
          <v-col cols="12" sm="4">
            <v-select label="Vendedor" item-text="nombre" item-value="cedula"
              v-model="reporte.cedula" :items="usuarios" :disabled="!validUsuarios"
              outlined rounded dense hide-details="auto" class="modern-input"
              prepend-inner-icon="mdi-account" />
          </v-col>
          <v-col cols="12" sm="3" class="d-flex" style="gap:10px;">
            <v-btn color="primary" rounded elevation="0" class="font-weight-bold px-6 flex-grow-1"
              :disabled="!valid" :loading="visibleProgress" @click="getReport()">
              <v-icon left size="16">mdi-chart-line</v-icon>Generar
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-card>

    <!-- KPI resumen -->
    <v-row v-if="validInfo" class="mb-4">
      <v-col v-for="kpi in kpis" :key="kpi.label" cols="6" sm="4" md="3" lg="2">
        <div :class="['kpi-mini', kpi.cls]">
          <div class="kpi-mini-label">{{ kpi.label }}</div>
          <div class="d-flex align-center justify-space-between">
            <div class="kpi-mini-value">{{ kpi.value }}</div>
            <!-- Ojo solo en KPIs con detalle -->
            <v-btn v-if="kpi.tipo" icon x-small class="kpi-eye-btn ml-1" @click.stop="verDetalle(kpi.tipo, kpi.label)">
              <v-icon size="16" color="white">mdi-eye-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Botones compartir -->
    <div v-if="validInfo" class="d-flex justify-end mb-3" style="gap:10px;">
      <v-btn rounded depressed class="font-weight-bold px-4" @click="dialogShare=true">
        <v-icon left size="18">mdi-text</v-icon>
        Texto
      </v-btn>
      <v-btn color="success" rounded depressed class="font-weight-bold px-5" :loading="generando" @click="generarImagen">
        <v-icon left size="18">mdi-image-outline</v-icon>
        Compartir imagen
      </v-btn>
    </div>

    <!-- Tabla de detalle -->
    <v-card v-if="validInfo" class="glass-card rounded-xl overflow-hidden" elevation="0">
      <div class="d-flex align-center px-5 pt-5 pb-3" style="gap:10px;">
        <div class="section-pill mr-2">
          <v-icon color="white" size="14">mdi-table</v-icon>
        </div>
        <span class="section-title">Detalle por Sorteo</span>
        <v-spacer />
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Filtrar..." outlined dense rounded
          hide-details class="modern-input" style="max-width:220px;" />
      </div>
      <v-divider style="opacity:0.08;" class="mx-5" />
      <v-data-table :headers="headers" :items="items" :search="search" class="modern-table"
        no-data-text="No hay datos para mostrar">
        <template v-slot:[`item.venta`]="{ item }">
          <span class="font-weight-bold">₡{{ fmt(item.venta) }}</span>
        </template>
        <template v-slot:[`item.premios`]="{ item }">
          <span class="color-red font-weight-bold">₡{{ fmt(item.premios) }}</span>
        </template>
        <template v-slot:[`item.comisionAdmin`]="{ item }">
          <span class="color-amber font-weight-bold">₡{{ fmt(item.comisionAdmin) }}</span>
        </template>
        <template v-slot:[`item.saldoSorteo`]="{ item }">
          <span :class="['font-weight-bold', item.saldoSorteo >= 0 ? 'color-green' : 'color-red']">
            ₡{{ fmt(item.saldoSorteo) }}
          </span>
        </template>
        <template v-slot:[`item.porcentajeSorteo`]="{ item }">
          <v-chip x-small label :color="item.porcentajeSorteo > 0 ? 'primary' : 'grey lighten-2'"
            class="font-weight-bold">
            {{ item.porcentajeSorteo }}%
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <!-- ── Modal compartir WhatsApp ── -->
    <v-dialog v-model="dialogShare" max-width="500" scrollable>
      <v-card class="rounded-xl overflow-hidden">
        <div class="share-header pa-5 d-flex align-center">
          <v-icon color="white" size="26" class="mr-3">mdi-whatsapp</v-icon>
          <div>
            <div class="share-title">Vista previa del resumen</div>
            <div class="share-subtitle">{{ vendedorNombre }} · Listo para compartir</div>
          </div>
          <v-spacer />
          <v-btn icon @click="dialogShare=false"><v-icon color="white">mdi-close</v-icon></v-btn>
        </div>
        <v-card-text class="pa-4">
          <div class="whatsapp-bubble pa-4 rounded-xl mb-4">
            <pre class="whatsapp-text">{{ whatsappText }}</pre>
          </div>
          <v-row dense>
            <v-col cols="6">
              <v-btn block depressed rounded class="font-weight-bold" @click="copiarTexto">
                <v-icon left size="18">{{ copiado ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                {{ copiado ? '¡Copiado!' : 'Copiar texto' }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn block depressed rounded color="success" class="font-weight-bold white--text" @click="abrirWhatsApp">
                <v-icon left size="18">mdi-whatsapp</v-icon>
                Abrir WhatsApp
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ── Modal detalle movimientos ── -->
    <v-dialog v-model="dialogDetalle" max-width="600" scrollable>
      <v-card class="rounded-xl overflow-hidden detalle-dialog">
        <!-- Header del modal -->
        <div class="detalle-header pa-5 d-flex align-center">
          <div class="detalle-icon-pill mr-3">
            <v-icon color="white" size="18">{{ detalleIcono }}</v-icon>
          </div>
          <div>
            <div class="detalle-title">{{ detalleTitle }}</div>
            <div class="detalle-subtitle">{{ vendedorNombre }} · {{ dates[0] }} ~ {{ dates[1] }}</div>
          </div>
          <v-spacer />
          <v-btn icon @click="dialogDetalle=false">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="pa-4">
          <!-- Loading -->
          <div v-if="loadingDetalle" class="d-flex justify-center align-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <!-- Sin datos -->
          <div v-else-if="detalleItems.length === 0" class="text-center py-8">
            <v-icon size="48" color="grey lighten-2">mdi-receipt-text-outline</v-icon>
            <div class="mt-2 text-body-2 grey--text">No hay movimientos en este período</div>
          </div>

          <!-- Tabla de movimientos -->
          <div v-else>
            <!-- Total destacado -->
            <div class="detalle-total-chip mb-4">
              <span class="text-caption font-weight-bold text-uppercase mr-2">Total</span>
              <span class="font-weight-black text-h6">₡{{ fmt(detalleTotal) }}</span>
              <span class="text-caption ml-2 grey--text">({{ detalleItems.length }} movimiento{{ detalleItems.length !== 1 ? 's' : '' }})</span>
            </div>

            <v-data-table
              :headers="headersDetalle"
              :items="detalleItems"
              :items-per-page="10"
              class="modern-table detalle-table"
              no-data-text="Sin movimientos"
              dense>
              <template v-slot:[`item.fecha`]="{ item }">
                <span class="text-caption">{{ fmtFecha(item.fecha) }}</span>
              </template>
              <template v-slot:[`item.monto`]="{ item }">
                <span :class="['font-weight-bold', detalleColor]">₡{{ fmt(item.monto) }}</span>
              </template>
              <template v-slot:[`item.descripcion`]="{ item }">
                <span class="text-caption">{{ item.descripcion || '—' }}</span>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ── Tarjeta oculta para imagen ── -->
    <div ref="cardImagen" class="card-imagen-root">
      <table class="ci-table">
        <thead>
          <tr>
            <td colspan="5" class="ci-title-cell">TIEMPOS — {{ vendedorNombre }}</td>
            <td class="ci-label-cell">SALDO ACTUAL:</td>
            <td class="ci-balance-cell">{{ balance }}</td>
          </tr>
          <tr>
            <td colspan="2" class="ci-date-cell">{{ datesText }}</td>
            <td class="ci-head-cell">VENTA</td>
            <td class="ci-head-cell">M. PREMIO</td>
            <td class="ci-head-cell">PREMIO</td>
            <td class="ci-head-cell">COMISIÓN</td>
            <td class="ci-head-cell">SALDO SORTEO</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.sorteo" class="ci-row">
            <td class="ci-pct-cell">{{ item.porcentajeSorteo }}%</td>
            <td class="ci-sorteo-cell">{{ item.sorteo }}</td>
            <td class="ci-num-cell">₡{{ fmt(item.venta) }}</td>
            <td class="ci-num-cell">₡{{ fmt(item.montoPremio) }}</td>
            <td class="ci-num-cell ci-red">₡{{ fmt(item.premios) }}</td>
            <td class="ci-num-cell ci-amber">₡{{ fmt(item.comisionAdmin) }}</td>
            <td :class="['ci-num-cell', item.saldoSorteo >= 0 ? 'ci-green-bg' : 'ci-red-bg']">₡{{ fmt(item.saldoSorteo) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="ci-total-row">
            <td colspan="2"></td>
            <td class="ci-num-cell">{{ venta }}</td>
            <td class="ci-num-cell"></td>
            <td class="ci-num-cell ci-red">{{ premios }}</td>
            <td class="ci-num-cell ci-amber">{{ comisionAdmin }}</td>
            <td class="ci-num-cell"></td>
          </tr>
          <tr class="ci-footer-row">
            <td colspan="2" class="ci-footer-label">ENTRADAS (recibido)</td>
            <td colspan="2" class="ci-footer-label">SALIDAS (pagado)</td>
            <td colspan="3" class="ci-footer-saldo-label">SALDO ACTUAL</td>
          </tr>
          <tr class="ci-footer-values">
            <td colspan="2" class="ci-footer-val">{{ entradas }}</td>
            <td colspan="2" class="ci-footer-val">{{ salidas }}</td>
            <td colspan="3" class="ci-footer-val ci-saldo-highlight">{{ balance }}</td>
          </tr>
        </tfoot>
      </table>
      <div class="ci-footer-brand">Generado por BestOne Tiempos</div>
    </div>
  </div>
</template>


<script>
import axios from "axios";
import { mapGetters } from "vuex";
import html2canvas from "html2canvas";

export default {
  name: "ReporteSaldos",
  data() {
    return {
      valid: false, validInfo: false, validUsuarios: false,
      visibleProgress: false, dateModal: false, search: "",
      dates: [], datesText: "", items: [], usuarios: [],
      venta:"", premios:"", comisionAdmin:"", totalAdmin:"", entradas:"", salidas:"", balance:"",
      dateRules: [() => (this.dates && this.dates.length === 2) || "Seleccione fecha inicial y final"],
      reporte: { cedula: "", fechaInicio: "", fechaFin: "" },
      headers: [
        { text: "%",          value: "porcentajeSorteo", width: 70  },
        { text: "Sorteo",     value: "sorteo"                       },
        { text: "Fecha",      value: "fechaSorteo"                  },
        { text: "Venta",      value: "venta"                        },
        { text: "# Ganador",  value: "numeroGanador",   width: 90  },
        { text: "Premio",     value: "montoPremio"                  },
        { text: "Premios",    value: "premios"                      },
        { text: "% Comisión", value: "porcentajeComision", width: 100 },
        { text: "Comisión",   value: "comisionAdmin"                },
        { text: "Saldo",      value: "saldoSorteo"                  },
      ],
      // Modal detalle
      dialogDetalle: false,
      loadingDetalle: false,
      detalleTitle: "",
      detalleIcono: "mdi-bank-transfer-in",
      detalleColor: "color-green",
      detalleItems: [],
      headersDetalle: [
        { text: "Fecha",       value: "fecha",       width: 110 },
        { text: "Descripción", value: "descripcion"             },
        { text: "Monto",       value: "monto",       width: 120, align: "right" },
      ],
      // Share
      dialogShare: false,
      copiado: false,
      generando: false,
      saldoAnterior: '',
    };
  },
  computed: {
    ...mapGetters(["user", "strCia"]),
    kpis() {
      return [
        { label: "Saldo Anterior", value: this.saldoAnterior, cls: "kpi-purple", tipo: null },
        { label: "Ventas",         value: this.venta,         cls: "kpi-blue",   tipo: null },
        { label: "Premios",        value: this.premios,       cls: "kpi-red",    tipo: null },
        { label: "Comisión",       value: this.comisionAdmin, cls: "kpi-amber",  tipo: null },
        { label: "Entradas",       value: this.entradas,      cls: "kpi-green",  tipo: 1    },
        { label: "Salidas",        value: this.salidas,       cls: "kpi-red",    tipo: 2    },
        { label: "Saldo Actual",   value: this.balance,       cls: "kpi-blue",   tipo: null },
      ];
    },
    detalleTotal() {
      return this.detalleItems.reduce((s, m) => s + (m.monto || 0), 0);
    },
    vendedorNombre() {
      const u = this.usuarios.find(u => u.cedula === this.reporte.cedula);
      return u ? u.nombre : this.reporte.cedula;
    },
    whatsappText() {
      const periodo = this.datesText || '';
      const vendedor = this.vendedorNombre;
      let msg = `🎰 *TIEMPOS - Resumen Banca*\n`;
      msg += `👤 *Vendedor:* ${vendedor}\n`;
      msg += `📅 ${periodo}\n`;
      msg += `\n`;
      msg += `💰 *Saldo Anterior:* ${this.saldoAnterior}\n`;
      msg += `💹 *Ventas:* ${this.venta}\n`;
      msg += `🏆 *Premios:* ${this.premios}\n`;
      msg += `💵 *Comisión Vendedor:* ${this.comisionAdmin}\n`;
      msg += `💰 *Entradas (recibido):* ${this.entradas}\n`;
      msg += `💸 *Salidas (pagado):* ${this.salidas}\n`;
      msg += `📊 *Saldo Actual:* ${this.balance}\n`;
      if (this.items && this.items.length > 0) {
        msg += `\n📋 *Detalle por sorteo:*\n`;
        this.items.forEach(item => {
          const v = item.venta ? `₡${this.fmt(item.venta)}` : '₡0';
          const s = item.saldoSorteo !== undefined ? ` | saldo ₡${this.fmt(item.saldoSorteo)}` : '';
          msg += `\u2022 ${item.sorteo}: ${v}${s}\n`;
        });
      }
      msg += `\n_Generado por BestOne Tiempos_ 🤝`;
      return msg;
    },
  },
  created() {
    this.reporte.cedula = this.user.cedula;
    this.getUsuariosPorCia();
  },
  methods: {
    async generarImagen() {
      this.generando = true;
      await this.$nextTick();
      const el = this.$refs.cardImagen;
      try {
        const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
        const dataUrl = canvas.toDataURL('image/png');
        if (navigator.canShare) {
          const blob = await (await fetch(dataUrl)).blob();
          const file = new File([blob], 'resumen-banca.png', { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: 'Resumen Tiempos Banca' });
            this.generando = false;
            return;
          }
        }
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = 'resumen-banca.png';
        a.click();
      } catch(e) { console.log(e); }
      this.generando = false;
    },
    copiarTexto() {
      navigator.clipboard.writeText(this.whatsappText).then(() => {
        this.copiado = true;
        setTimeout(() => { this.copiado = false; }, 2500);
      });
    },
    abrirWhatsApp() {
      window.open('https://wa.me/?text=' + encodeURIComponent(this.whatsappText), '_blank');
    },
    getReport() { this.visibleProgress = true; this.getReporteVentas(); },
    getReporteVentas() {
      // Limpiar datos anteriores antes del nuevo request
      this.validInfo = false;
      this.items = [];
      this.saldoAnterior = '';
      this.venta = this.premios = this.comisionAdmin = this.entradas = this.salidas = this.balance = '';
      axios.get(`api/Tiempos/GetReporteSaldo?idUsuario=${this.reporte.cedula}&fechaInicio=${this.dates[0]}&fechaFin=${this.dates[1]}&companyId=${this.strCia}`)
        .then(r => {
          this.visibleProgress = false;
          if (!r.data || r.data.length === 0) { this.validInfo = false; return; }
          const resumen = r.data[0];
          this.items = r.data.slice(1);
          this.venta        = `₡${this.fmt(resumen.venta)}`;
          this.premios      = `₡${this.fmt(resumen.premios)}`;
          this.comisionAdmin= `₡${this.fmt(resumen.comisionAdmin)}`;
          this.entradas     = `₡${this.fmt(resumen.totalEntradas)}`;
          this.salidas      = `₡${this.fmt(resumen.totalSalidas)}`;
          this.balance      = `₡${this.fmt(resumen.balance)}`;
          this.saldoAnterior= `₡${this.fmt(resumen.saldoAnterior)}`;
          this.validInfo = true;
        })
        .catch(e => { console.log(e); this.visibleProgress = false; });
    },
    getUsuariosPorCia() {
      axios.get(`api/Usuarios/getUsuariosPorCia?companyId=${this.strCia}`)
        .then(r => {
          this.usuarios = r.data.filter(u => u.cedula && u.cedula !== "0");
          const me = this.usuarios.find(u => u.cedula === this.user.cedula);
          if (me && me.tipo === true) {
            this.reporte.cedula = this.usuarios.length > 0 ? this.usuarios[0].cedula : "";
            this.validUsuarios = true;
          } else if (me) {
            this.reporte.cedula = me.cedula;
          }
        }).catch(e => console.log(e));
    },

    verDetalle(tipo, label) {
      this.dialogDetalle = true;
      this.loadingDetalle = true;
      this.detalleItems = [];
      this.detalleTitle = label;
      this.detalleIcono = tipo === 1 ? "mdi-bank-transfer-in" : "mdi-bank-transfer-out";
      // Desde la perspectiva del banquero: entrada = recibió dinero (verde), salida = pagó (rojo)
      this.detalleColor = tipo === 1 ? "color-green" : "color-red";

      axios.get(`api/Tiempos/GetMovimientosVendedor?companyId=${this.strCia}&idUsuario=${this.reporte.cedula}&tipo=${tipo}&fechaInicio=${this.dates[0]}&fechaFin=${this.dates[1]}`)
        .then(r => {
          this.detalleItems = r.data || [];
          this.loadingDetalle = false;
        })
        .catch(() => { this.loadingDetalle = false; });
    },

    fmt(v) {
      if (v === undefined || v === null || v === "") return "0";
      const n = Number(v);
      return isNaN(n) ? "0" : n.toLocaleString("es-CR");
    },
    fmtFecha(v) {
      if (!v) return "—";
      const d = new Date(v);
      const pad = n => String(n).padStart(2, "0");
      return `${pad(d.getDate())}-${pad(d.getMonth()+1)}-${d.getFullYear()}`;
    },
    setDatesFormat() {
      if (!this.dates || this.dates.length < 2) return;
      const fmt = (s) => { const [y, m, d] = s.split('-'); return `${d}-${m}-${y}`; };
      this.datesText = `${fmt(this.dates[0])} ~ ${fmt(this.dates[1])}`;
      this.$refs.form && this.$refs.form.validate();
    },
  },
};
</script>

<style scoped>
.reporte-page { padding: 20px 24px; width: 100%; }
.page-header { animation: fadeIn 0.4s ease; }
.header-icon-pill {
  width: 46px; height: 46px; border-radius: 14px;
  background: linear-gradient(135deg, #7C3AED, #A78BFA);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(124,58,237,0.35); flex-shrink: 0;
}
.page-title { font-size: 1.3rem; font-weight: 800; letter-spacing: -0.3px; }
.page-subtitle { font-size: 0.8rem; color: #64748b; }
.theme--dark .page-subtitle { color: #94a3b8; }

.glass-card {
  background: rgba(255,255,255,0.75) !important;
  backdrop-filter: blur(16px) !important;
  border: 1px solid rgba(255,255,255,0.5) !important;
}
.theme--dark .glass-card { background: rgba(30,30,46,0.75) !important; border-color: rgba(255,255,255,0.08) !important; }

.section-pill {
  width: 26px; height: 26px; border-radius: 8px;
  background: linear-gradient(135deg, #7C3AED, #A78BFA);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.section-title { font-size: 0.9rem; font-weight: 700; }

/* KPI mini cards */
.kpi-mini {
  border-radius: 14px; padding: 14px 16px;
  transition: transform 0.2s ease;
}
.kpi-mini:hover { transform: translateY(-2px); }
.kpi-blue  { background: linear-gradient(135deg,#1976D2,#42A5F5); box-shadow: 0 6px 18px rgba(25,118,210,0.3); }
.kpi-red   { background: linear-gradient(135deg,#DC2626,#F87171); box-shadow: 0 6px 18px rgba(220,38,38,0.3); }
.kpi-amber { background: linear-gradient(135deg,#D97706,#FBBF24); box-shadow: 0 6px 18px rgba(217,119,6,0.3); }
.kpi-green { background: linear-gradient(135deg,#059669,#34D399); box-shadow: 0 6px 18px rgba(5,150,105,0.3); }
.kpi-purple{ background: linear-gradient(135deg,#7C3AED,#A78BFA); box-shadow: 0 6px 18px rgba(124,58,237,0.3); }
.kpi-mini-label { font-size: 0.65rem; font-weight: 600; color: rgba(255,255,255,0.8); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
.kpi-mini-value { font-size: 1rem; font-weight: 900; color: white; letter-spacing: -0.3px; }
.kpi-eye-btn { opacity: 0.75; transition: opacity 0.2s, transform 0.2s; }
.kpi-eye-btn:hover { opacity: 1; transform: scale(1.15); }

/* Table */
.modern-table >>> tbody tr:hover { background: rgba(124,58,237,0.04) !important; }
.modern-table >>> th {
  font-size: 0.72rem !important; font-weight: 700 !important;
  text-transform: uppercase; letter-spacing: 0.5px;
  color: #64748b !important; border-bottom: 1px solid rgba(0,0,0,0.06) !important;
}
.theme--dark .modern-table >>> th { color: #94a3b8 !important; }

.color-green { color: #059669; }
.color-red   { color: #DC2626; }
.color-amber { color: #D97706; }
.theme--dark .color-green { color: #34D399; }
.theme--dark .color-red   { color: #F87171; }
.theme--dark .color-amber { color: #FBBF24; }

.modern-input >>> .v-input__slot { background: rgba(255,255,255,0.6) !important; transition: all 0.2s; }
.modern-input >>> .v-input__slot:hover { background: rgba(255,255,255,0.95) !important; }
.theme--dark .modern-input >>> .v-input__slot { background: rgba(0,0,0,0.2) !important; }

/* Modal detalle */
.detalle-dialog { overflow: hidden; }
.detalle-header {
  background: linear-gradient(135deg, #7C3AED, #A78BFA);
}
.detalle-icon-pill {
  width: 36px; height: 36px; border-radius: 10px;
  background: rgba(255,255,255,0.25);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.detalle-title { font-size: 1rem; font-weight: 800; color: white; }
.detalle-subtitle { font-size: 0.75rem; color: rgba(255,255,255,0.8); }
.detalle-total-chip {
  background: rgba(124,58,237,0.08);
  border: 1px solid rgba(124,58,237,0.2);
  border-radius: 10px;
  padding: 10px 16px;
  display: flex;
  align-items: center;
}
.detalle-table >>> th {
  font-size: 0.7rem !important; font-weight: 700 !important;
  text-transform: uppercase; letter-spacing: 0.5px;
  color: #64748b !important;
}

@keyframes fadeIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }

/* Modal compartir WhatsApp */
.share-header { background: linear-gradient(135deg, #25D366, #128C7E); }
.share-title { font-size: 1rem; font-weight: 800; color: white; }
.share-subtitle { font-size: 0.75rem; color: rgba(255,255,255,0.85); }
.whatsapp-bubble {
  background: #ECF5EC; border: 1px solid #d4edda;
  min-height: 100px; max-height: 350px; overflow-y: auto;
}
.theme--dark .whatsapp-bubble { background: #1e2d1e; border-color: #2d4a2d; }
.whatsapp-text {
  font-family: 'Segoe UI', sans-serif; font-size: 0.82rem; line-height: 1.6;
  white-space: pre-wrap; word-break: break-word; color: #111; margin: 0;
}
.theme--dark .whatsapp-text { color: #e2e8e2; }

/* Tarjeta imagen oculta */
.card-imagen-root {
  position: absolute; left: -9999px; top: 0;
  background: #ffffff; padding: 12px; width: 640px;
  font-family: Arial, Helvetica, sans-serif;
}
.ci-table { width: 100%; border-collapse: collapse; font-size: 11px; }
.ci-table td { border: 1px solid #bbb; padding: 4px 6px; }
.ci-title-cell { text-align: center; font-weight: 900; font-size: 13px; }
.ci-label-cell { text-align: right; font-size: 10px; color: #555; }
.ci-balance-cell { text-align: right; font-weight: bold; background: #fca5a5; }
.ci-date-cell { background: #fef08a; font-weight: bold; }
.ci-head-cell { font-weight: bold; background: #e5e7eb; text-align: center; }
.ci-pct-cell { text-align: center; background: #f9fafb; font-weight: bold; }
.ci-sorteo-cell { font-weight: 600; }
.ci-num-cell { text-align: right; }
.ci-red { color: #dc2626; }
.ci-amber { color: #d97706; font-weight: bold; }
.ci-green-bg { background: #d1fae5; color: #065f46; font-weight: bold; }
.ci-red-bg { background: #fee2e2; color: #991b1b; font-weight: bold; }
.ci-total-row td { font-weight: bold; background: #f3f4f6; border-top: 2px solid #888; }
.ci-footer-row td { text-align: center; font-size: 10px; font-weight: bold; background: #e5e7eb; padding: 3px; }
.ci-footer-label { text-align: center; font-size: 10px; }
.ci-footer-val { text-align: center; font-weight: bold; font-size: 12px; padding: 6px; }
.ci-saldo-highlight { background: #fef08a; font-size: 14px; font-weight: 900; color: #92400e; }
.ci-footer-brand { text-align: right; font-size: 9px; color: #9ca3af; margin-top: 4px; }
</style>